import React, { useMemo, useRef, useState } from "react";
import { api } from "../../../software/api";
import { toast } from "sonner";
import { printStartOfDayDocket } from "../reports/legacy/docs";

// Australian denominations, highest -> lowest (matches the legacy Forms_CashDrawer)
const DENOMS = [
  { key: "100", label: "$100.00", value: 100 },
  { key: "50", label: "$50.00", value: 50 },
  { key: "20", label: "$20.00", value: 20 },
  { key: "10", label: "$10.00", value: 10 },
  { key: "5", label: "$5.00", value: 5 },
  { key: "2", label: "$2.00", value: 2 },
  { key: "1", label: "$1.00", value: 1 },
  { key: "0.5", label: "$0.50c", value: 0.5 },
  { key: "0.2", label: "$0.20c", value: 0.2 },
  { key: "0.1", label: "$0.10c", value: 0.1 },
  { key: "0.05", label: "$0.05c", value: 0.05 },
];

const money = (n) => `$${Number(n || 0).toFixed(2)}`;


export default function StartDayFloat({ clerk, company, onDone, onClose }) {
  const [denoms, setDenoms] = useState({});
  const [manualTotal, setManualTotal] = useState("");
  const [saving, setSaving] = useState(false);
  const totalInputRef = useRef(null);

  const denomTotal = useMemo(
    () => DENOMS.reduce((s, d) => s + (Number(denoms[d.key]) || 0) * d.value, 0),
    [denoms]
  );
  // If the user has typed a lump-sum total, that wins; otherwise the denomination sum.
  const total = manualTotal !== "" ? Number(manualTotal) || 0 : denomTotal;

  const setQty = (key, v) => {
    setManualTotal(""); // typing a denomination clears any lump-sum override
    setDenoms((d) => ({ ...d, [key]: v.replace(/[^0-9]/g, "") }));
  };

  const save = async () => {
    if (saving) return;
    if (total <= 0) { toast.error("Enter the start-of-day float amount."); return; }
    setSaving(true);
    try {
      const res = await api("POST", "/cashdrawer/start-day", {
        float_amount: Number(total.toFixed(2)),
        denominations: denoms,
      });
      if (res.already_started) {
        toast.info("A start-of-day float was already recorded today.");
      } else {
        printStartOfDayDocket(clerk, total, company);
        try { await api("POST", "/pos/open-drawer"); } catch (e) { /* non-fatal */ }
        toast.success(`Start of day float saved — ${money(total)}. Till drawer opening.`);
      }
      onDone && onDone();
    } catch (e) {
      toast.error(e.message || "Could not save the float.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="start-day-float-overlay">
      <div
        className="relative w-full max-w-4xl rounded-lg overflow-hidden shadow-2xl border border-black/40"
        style={{ backgroundImage: "url('/cash-drawer-bg.png')", backgroundSize: "cover", backgroundPosition: "center" }}
        data-testid="start-day-float"
      >
        {/* dark wash so text reads over the photo */}
        <div className="absolute inset-0 bg-black/45" />
        <div className="relative grid grid-cols-[180px_1fr]">
          {/* LEFT — denomination ladder */}
          <div className="bg-black/70 p-2 space-y-1.5" data-testid="float-denoms">
            {DENOMS.map((d) => {
              const qty = Number(denoms[d.key]) || 0;
              return (
                <div key={d.key} className="leading-none">
                  <div className="flex items-center justify-between gap-1.5">
                    <span className="text-white font-black text-[14px] w-[64px] text-right">{d.label}</span>
                    <input
                      value={denoms[d.key] || ""}
                      onChange={(e) => setQty(d.key, e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") save(); }}
                      inputMode="numeric"
                      data-testid={`float-qty-${d.key}`}
                      className="w-[52px] bg-[#fff7b0] border border-black/50 px-1.5 py-1 text-[13px] font-mono2 text-center outline-none focus:ring-2 focus:ring-amber-400"
                    />
                  </div>
                  <div className="text-right text-amber-300 underline text-[12px] font-mono2 pr-1 mt-0.5" data-testid={`float-line-${d.key}`}>{money(qty * d.value)}</div>
                </div>
              );
            })}
          </div>

          {/* RIGHT — heading, lump-sum total, instructions */}
          <div className="relative p-6 flex flex-col">
            <div className="flex items-start justify-between">
              <h2 className="text-white font-black text-2xl underline drop-shadow tracking-wide" data-testid="float-title">START OF DAY CASH DRAWER FLOAT</h2>
              <button onClick={onClose} data-testid="float-cancel" title="Cancel" className="ml-3 text-white/80 hover:text-white text-sm font-bold border border-white/40 rounded px-2 py-1">Cancel</button>
            </div>

            <div className="mt-8 flex justify-center">
              <input
                ref={totalInputRef}
                value={manualTotal !== "" ? manualTotal : (denomTotal > 0 ? denomTotal.toFixed(2) : "")}
                onChange={(e) => setManualTotal(e.target.value.replace(/[^0-9.]/g, ""))}
                onKeyDown={(e) => { if (e.key === "Enter") save(); }}
                placeholder="0.00"
                inputMode="decimal"
                data-testid="float-total-input"
                className="w-[320px] max-w-full bg-[#fff7b0] border border-black/60 px-4 py-3 text-2xl font-black font-mono2 text-center outline-none focus:ring-2 focus:ring-amber-400"
              />
            </div>

            <p className="text-center text-white font-black text-lg underline mt-10 leading-snug drop-shadow max-w-md mx-auto">
              Simply insert the Till Start Float Amount then press the 'ENTER' button on the keyboard.
            </p>

            <div className="mt-auto pt-8 flex items-center justify-center gap-3">
              <div className="bg-black/60 text-white text-sm px-3 py-1.5 rounded">Clerk: <b>{clerk || "—"}</b></div>
              <button onClick={save} disabled={saving} data-testid="float-save-btn"
                className="rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-black text-lg px-8 py-3 shadow-lg">
                {saving ? "Saving…" : "SAVE FLOAT & OPEN TILL"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
